import { fixNumber } from "@/components/Mission/MissionStat";
import {
  HouseEventLocation,
  HouseSessionEventStatus,
} from "@/enums/houseEventEnums";
import api from "@/modules/api";
import { entityStoreFactory } from "@/stores/factory/entity-store-factory";
import {
  cursorEncoderById,
  cursorPagerStoreFactory,
  pageStoreFactory,
} from "@/stores/factory/list-store-factory";
import { useGlobalUser } from "@/stores/globalUser";
import { useMate } from "@/stores/mate";
import {
  HouseEvent,
  MateHouseSession,
  MateHouseSessionEvent,
  MateHouseSessionMessage,
} from "@/types/entity";
import { useIntervalFn } from "@vueuse/core";
import { defineStore } from "pinia";
import { computed, reactive, ref, watch } from "vue";

const PER_PAGE = 10;
const MAX_COUNT_PER_HOUSE = 5;

export type HouseSessionTab =
  | "members"
  | "thoughts"
  | "subject"
  | "events"
  | "interaction";

export const useHouseSession = defineStore("mateHouseSession", () => {
  const activeTab = ref<HouseSessionTab>("members");
  const houseSession = ref<MateHouseSession | null>(null);
  /** mate维度消息列表 */
  const mateId2MessageList = reactive<
    Record<string, MateHouseSessionMessage[]>
  >({});
  const entityState = entityStoreFactory<MateHouseSessionMessage>("id");
  const { updateMap } = entityState;
  /** 索引: start */
  const mateId2HeadMsg = computed(() => {
    const record: Record<string, MateHouseSessionMessage | null> = {};
    const messageList = fullMessageList.value.slice(
      0,
      Math.min(MAX_COUNT_PER_HOUSE, fullMessageList.value.length)
    );
    messageList.forEach((m) => {
      if (!record[m.mateId!]) {
        record[m.mateId!] = m;
      }
    });
    return record;
  });
  const mateId2SpriteId = computed<Record<string, number>>(() => {
    const record: Record<string, number> = {};
    houseSession.value?.mateSprites?.forEach((m) => {
      record[m.mateId!] = m.id!;
    });
    return record;
  });
  const spriteId2MateId = computed(() => {
    const record: Record<string, number> = {};
    houseSession.value?.mateSprites?.forEach((m) => {
      record[m.id!] = m.mateId!;
    });
    return record;
  });
  /** 索引: end */
  const mateStore = useMate();

  const {
    state: fullMessageListState,
    dataList: fullMessageList,
    fetchNewerList: fetchNewerFullList,
    fetchOlderListWithCount: fetchOlderFullList,
    initListIfAbsent: initFullListIfAbsent,
    resetState: resetFullData,
  } = cursorPagerStoreFactory<
    MateHouseSessionMessage,
    { isGenerateIdeas?: 0 | 1 }
  >({
    entityStore: entityState,
    fetch: (cursor?: string, options?: { isGenerateIdeas?: 0 | 1 }) =>
      fetchMessageList({ cursor, isGenerateIdeas: options?.isGenerateIdeas }),
    cursorEncoder: cursorEncoderById,
  });

  const isThoughtActivated = computed(() => {
    return (
      houseSession.value?.mateSprites.some(
        (item) =>
          mateStore.getMyMateById(item.mateId!)?.isHouseEnabled ??
          item.mate?.isHouseEnabled
      ) && useGlobalUser().user.powerAmount > 2
    );
  });

  async function fetchMessageList(param: {
    cursor?: string;
    mateId?: number;
    perPage?: number;
    isGenerateIdeas?: 0 | 1;
  }) {
    const { cursor, mateId, perPage = PER_PAGE } = param;
    const res = await api.readMateHouseSessionMessagePage({
      mateId: mateId ?? undefined,
      pagination: { perPage },
      isGenerateIdeas: param.isGenerateIdeas,
      sort: {
        field: "id",
        order: "desc",
      },
      cursor,
    });
    updateMap(res.page.data);

    if (mateId2MessageList[mateId!])
      mateId2MessageList[mateId!].unshift(...res.page.data);

    if (res.page.data.length > 0) useGlobalUser().refresh();

    return res.page.data;
  }

  async function getOrFetchMateMessageList(
    mateId: number,
    force: boolean = false
  ) {
    if (!force && mateId2MessageList[mateId]) {
      return mateId2MessageList[mateId];
    }
    const list = await fetchMessageList({ mateId });
    mateId2MessageList[mateId] = list;
    return list;
  }

  async function getNewerMateMessageList(
    mateId: number,
    perPage?: number
  ): Promise<void> {
    const head = mateId2MessageList[mateId]?.[0];
    if (!head) {
      const list = await getOrFetchMateMessageList(mateId, true);
      mateId2MessageList[mateId] = list;
      return;
    }
    const list = await fetchMessageList({
      cursor: cursorEncoderById(head, false),
      mateId,
      perPage,
    });
    if (list.length > 0) useGlobalUser().refresh();

    mateId2MessageList[mateId].unshift(...list);
  }

  async function getOlderMateMessageList(mateId: number): Promise<{
    hasMore: boolean;
    count: number;
  }> {
    const tail =
      mateId2MessageList[mateId]?.[mateId2MessageList[mateId].length - 1];
    if (!tail) {
      const list = await getOrFetchMateMessageList(mateId, true);
      mateId2MessageList[mateId] = list;
      return { hasMore: list.length !== 0, count: list.length };
    }
    const list = await fetchMessageList({
      cursor: cursorEncoderById(tail, true),
      mateId,
    });
    mateId2MessageList[mateId].push(...list);
    if (list.length === 0 || list.length < PER_PAGE) {
      return { hasMore: false, count: 0 };
    }
    return { hasMore: true, count: list.length };
  }

  async function updateHouseSession(spriteIds: number[]) {
    if (!houseSession.value) {
      return;
    }
    const { item } = await api.updateOneMateHouseSession(
      houseSession.value.uuid,
      {
        mateSpriteIds: spriteIds,
      }
    );
    houseSession.value = item;
  }

  // 是否执行了清空消息的操作
  const subjectUpdateTime = ref<null | number>(null);
  const clearAllMessageList = () => {
    // 1. 清空mateId2MessageList
    Object.keys(mateId2MessageList).forEach((key) => {
      mateId2MessageList[key] = [];
    });
    // 2. 清空fullMessageList
    resetFullData();
  };
  async function deleteHouseSessionMessage(messageId?: number) {
    if (!houseSession.value) {
      return;
    }
    await api.deleteMateHouseSessionMessage(houseSession.value.id, messageId);
    if (messageId === undefined) {
      clearAllMessageList();
    }
  }

  async function updateHouseSessionSubject(subject?: string) {
    if (!houseSession.value) {
      return;
    }
    const { item } = await api.updateOneMateHouseSession(
      houseSession.value.uuid,
      { subject }
    );
    houseSession.value = item;
  }

  async function updateHouseSessionIsAutoUpdateTopic(
    isAutoUpdateTopic?: boolean
  ) {
    if (!houseSession.value) {
      return;
    }
    const { item } = await api.updateOneMateHouseSession(
      houseSession.value.uuid,
      { isAutoUpdateTopic }
    );
    houseSession.value = item;
  }

  async function addSpriteToHouseSession(spriteId: number) {
    if (!houseSession.value) {
      return;
    }
    const oldSpriteIds = houseSession.value.mateSpriteIds ?? [];
    if (oldSpriteIds.includes(spriteId)) {
      return;
    }
    updateHouseSession([...oldSpriteIds, spriteId]);
  }

  async function getOrFetchHouseSession(uuid?: string, force = false) {
    uuid = uuid ?? useGlobalUser().user.currentMateHouseSession.uuid;
    if (!force && houseSession.value?.uuid === uuid) {
      return houseSession.value;
    }
    const { item } = await api.getOneMateHouseSession(uuid);
    houseSession.value = item;
    return item;
  }

  async function toggleAllActiveThought(active: boolean) {
    const mateInHouse =
      houseSession.value?.mateSprites.map((sprite) =>
        mateStore.getMyMateById(sprite.mateId!)
      ) ?? [];
    const toChangeMate = mateInHouse.filter(
      (m) => active !== m?.isHouseEnabled
    );
    if (toChangeMate.length === 0) {
      return;
    }
    await Promise.all(
      toChangeMate.map((m) =>
        mateStore.updateMateDetail(m!.uuid!, {
          isHouseEnabled: active,
        })
      )
    );
  }

  /* @region House Event */
  async function createEvent(
    spriteIds: number[],
    endingId?: number,
    isGenerateAudio: boolean = false
  ) {
    const { item } = await api.createOneMateHouseSessionEvent({
      spriteIds,
      endingId,
      isMessageAudioNeeded: isGenerateAudio ? 1 : 0,
    });
    updateSessionEventMap([item]);
    return item;
  }

  const eventEntityState = entityStoreFactory<HouseEvent>("id");
  const sessionEventEntityState =
    entityStoreFactory<MateHouseSessionEvent>("id");
  const { updateMap: updateSessionEventMap, map: sessionEventMap } =
    sessionEventEntityState;
  const { updateMap: updateEventMap } = eventEntityState;
  const {
    dataList: eventList,
    fetchPreviousPage: fetchPreviousEventList,
    fetchNextPage: fetchNextEventList,
    initListIfAbsent: initEventListIfAbsent,
  } = pageStoreFactory<HouseEvent, { location?: HouseEventLocation }>({
    entityStore: eventEntityState,
    fetch: async (page: number) => {
      const res = await api.readHouseEventPage({
        pagination: { page, perPage: PER_PAGE },
        filter: {
          location: HouseEventLocation.House,
        },
      });
      res.page.data.forEach((event) => {
        updateEventMap([event]);
        event.endingzz.forEach((ending) => {
          ending.completedHouseSessionEventzz = updateSessionEventMap(
            ending.completedHouseSessionEventzz
          );
        });
      });

      return res;
    },
  });

  const fetchSessionEvent = async (
    eventId: number
  ): Promise<MateHouseSessionEvent> => {
    const { item } = await api.readOneMateHouseSessionEvent(eventId);
    updateSessionEventMap([item]);
    return item;
  };

  const updateSessionEvent = async (
    id: number,
    params: Parameters<typeof api.updateOneMateHouseSessionEvent>[1]
  ) => {
    const { item } = await api.updateOneMateHouseSessionEvent(id, params);
    const old = sessionEventMap.value[id]!;
    Object.assign(old, { ...params });
    return item;
  };

  const generatedButNotPlayedEvent = computed<MateHouseSessionEvent | null>(
    () => {
      return (
        Object.values(sessionEventMap.value).find(
          (e) =>
            e.status === HouseSessionEventStatus.Completed &&
            e.isPlayed === false
        ) ?? null
      );
    }
  );
  const generatingSessionEventId = computed<number | null>(() => {
    return (
      Object.values(sessionEventMap.value).find(
        (e) =>
          e.status !== HouseSessionEventStatus.Completed &&
          e.status !== HouseSessionEventStatus.Failed
      )?.id ?? null
    );
  });

  const hasGeneratingEvent = computed(() => {
    return generatingSessionEventId.value !== null;
  });

  const { pause, resume } = useIntervalFn(
    async () => {
      const targetSessionEventId = generatingSessionEventId.value;
      if (!targetSessionEventId) return;
      const sessionEvent = await fetchSessionEvent(targetSessionEventId);
      if (sessionEvent.status === HouseSessionEventStatus.Completed) {
        await initEventListIfAbsent(true);
      }
    },
    3000,
    {
      immediate: false,
      immediateCallback: false,
    }
  );
  watch(
    hasGeneratingEvent,
    (newVal) => {
      if (newVal) {
        resume();
      } else {
        pause();
      }
    },
    {
      immediate: true,
    }
  );
  /* @endregion */

  const extraPower = computed(() => {
    if (!houseSession.value?.subject) return 0;
    return fixNumber(
      Math.ceil((houseSession.value?.subject?.length - 100) / 100) * 0.1
    );
  });

  return {
    houseSession,
    activeTab,
    mateId2MessageList,
    fullMessageListState,
    fullMessageList,
    mateId2HeadMsg,
    mateId2SpriteId,
    spriteId2MateId,
    isThoughtActivated,
    subjectUpdateTime,
    initFullListIfAbsent,
    fetchNewerFullList,
    fetchOlderFullList,
    getOrFetchMateMessageList,
    getNewerMateMessageList,
    getOlderMateMessageList,
    updateHouseSession,
    updateHouseSessionSubject,
    getOrFetchHouseSession,
    deleteHouseSessionMessage,
    resetFullData,
    addSpriteToHouseSession,
    toggleAllActiveThought,
    updateHouseSessionIsAutoUpdateTopic,
    /* @region House Event */
    createEvent,
    eventList,
    sessionEventMap,
    updateSessionEventMap,
    fetchPreviousEventList,
    fetchNextEventList,
    initEventListIfAbsent,
    fetchSessionEvent,
    updateSessionEvent,
    generatedButNotPlayedEvent,
    generatingSessionEventId,
    hasGeneratingEvent,
    /* @endregion */
    extraPower,

    // 管理弹框避免跟 PhaserGame 事件冒泡
    showModelPopup: ref(false), // 模型选择弹框
    showSpriteSelector: ref(false), // 成员选择弹框
    showEditSubjectPopup: ref(false), // 话题编辑弹框
    showEventSpriteSelectorPopup: ref(false), // 事件成员选择弹框
    showEventEndingDetailPopup: ref(false), // 事件结局详情弹框
    showHouseSetting: ref(false), // 互动池设置弹框
  };
});
